/**
 * CR-8613: sidebar items that open one tab of another item's page — "Call Off"
 * is `/purchasing?tab=call_off`, beside Purchasing's `/purchasing`.
 *
 * The sidebar's path matching ignores the query string, so on its own it would
 * light up Purchasing on the call-off tab and never Call Off. These two rules
 * give the tab to its own item, and only to it.
 */

/**
 * Whether a query-string href is the current location: the same path and the
 * same `tab`. Only the tab counts — the item's other parameters (which view of
 * the tab it opens) and a deep link's (`calloff_id`) say nothing about which
 * tab the user is on, and arriving through the page's own tab strip carries
 * none of them.
 *
 * @example tabHrefMatches('/purchasing?tab=call_off&call_off_view=management', '/purchasing', '?tab=call_off&calloff_id=4') // true
 * @param href - the item's href, carrying a `?tab=`
 * @param pathname - the current path
 * @param search - the current query string
 * @returns true when the location is that tab
 */
export function tabHrefMatches(href: string, pathname: string, search: string): boolean {
  const [path, query] = href.split('?');
  if (!query || pathname !== path) return false;
  const tab = new URLSearchParams(query).get('tab');
  return tab !== null && new URLSearchParams(search).get('tab') === tab;
}

/**
 * Whether the location is a tab that another sidebar item claims, so the
 * page's own item (`href`) must not light up as well.
 *
 * @param href - the page item's href, without a query
 * @param hrefs - every href in the sidebar
 * @param pathname - the current path
 * @param search - the current query string
 * @returns true when a tab item owns the location
 */
export function claimedByTabItem(
  href: string, hrefs: readonly string[], pathname: string, search: string,
): boolean {
  return hrefs.some(other => other.startsWith(`${href}?`)
    && tabHrefMatches(other, pathname, search));
}
